import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { clients as clientsApi, coffeeDispatches } from '../lib/api';
import { inr } from '../lib/format';
import { Users, Search, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';

const LevelBadge = ({ level }) => (
  <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded-full font-semibold ${
    level === 'Light' ? 'bg-yellow-100 text-yellow-800' :
    level === 'Dark' ? 'bg-stone-700 text-white' : 'bg-amber-100 text-amber-800'
  }`}>{level}</span>
);

export const ClientsTab = () => {
  const [clientRows, setClientRows] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [c, d] = await Promise.all([clientsApi.getAll(), coffeeDispatches.getAll()]);
      setClientRows(c.data);
      setDispatches(d.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(
    () => clientRows.filter((c) => c.name.toLowerCase().includes(search.toLowerCase())),
    [clientRows, search]
  );

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"?`)) return;
    try {
      await clientsApi.delete(c.id);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete client');
    }
  };

  if (loading) {
    return <div className="py-12 text-center text-gray-500">Loading clients…</div>;
  }

  return (
    <>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search clients…"
          data-testid="client-search-input"
          className="w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>{search ? 'No clients match your search.' : 'No clients yet. Add a client name when logging a dispatch.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Client</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Dispatches</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Total Kg</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Total Sales ₹</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Est. Cost ₹</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Gross Profit ₹</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Outstanding ₹</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Last Dispatch</th>
                  <th className="text-center py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const isOpen = expanded === c.id;
                  const clientDispatches = dispatches.filter((d) => d.client_name === c.name);
                  return (
                    <React.Fragment key={c.id}>
                      <tr className="border-t hover:bg-gray-50 cursor-pointer" data-testid={`client-row-${c.id}`}
                        onClick={() => setExpanded(isOpen ? null : c.id)}>
                        <td className="py-3 px-4 font-semibold text-gray-800">
                          <span className="inline-flex items-center gap-1.5">
                            {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                            {c.name}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">{c.dispatch_count}</td>
                        <td className="py-3 px-4 text-right font-semibold text-blue-600">{c.total_kg.toFixed(2)}</td>
                        <td className="py-3 px-4 text-right font-semibold text-gray-800">{c.total_sale_value > 0 ? inr(c.total_sale_value) : '-'}</td>
                        <td className="py-3 px-4 text-right text-gray-600">{c.total_cost_value > 0 ? inr(c.total_cost_value) : '-'}</td>
                        <td className={`py-3 px-4 text-right font-semibold ${c.gross_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {c.total_sale_value > 0 ? (
                            <>
                              {inr(c.gross_profit)}
                              <p className="text-[10px] text-gray-400 font-normal">
                                {((c.gross_profit / c.total_sale_value) * 100).toFixed(1)}% margin
                              </p>
                            </>
                          ) : '-'}
                        </td>
                        <td className={`py-3 px-4 text-right font-semibold ${(c.outstanding || 0) > 0 ? 'text-rose-600' : 'text-emerald-600'}`} data-testid={`client-outstanding-${c.id}`}>
                          {c.total_sale_value > 0 ? inr(c.outstanding || 0) : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {c.last_dispatch_date ? (() => {
                            const days = differenceInDays(new Date(), new Date(c.last_dispatch_date));
                            return (
                              <>
                                {format(new Date(c.last_dispatch_date), 'MMM dd, yyyy')}
                                <p className={`text-[10px] ${days > 30 ? 'text-rose-600 font-semibold' : 'text-gray-400'}`}>
                                  {days === 0 ? 'today' : `${days}d ago`}{days > 30 ? ' — going quiet' : ''}
                                </p>
                              </>
                            );
                          })() : '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {c.dispatch_count === 0 && (
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(c); }}
                              data-testid={`delete-client-${c.id}`}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete client">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t bg-gray-50/60">
                          <td colSpan={9} className="py-3 px-6">
                            {clientDispatches.length === 0 ? (
                              <p className="text-sm text-gray-500">No dispatches yet for this client.</p>
                            ) : (
                              <div className="space-y-2" data-testid={`client-dispatch-history-${c.id}`}>
                                {clientDispatches.map((d) => (
                                  <div key={d.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm bg-white border rounded-lg px-3 py-2">
                                    <span className="text-gray-500 whitespace-nowrap">{format(new Date(d.dispatch_date), 'MMM dd, yyyy')}</span>
                                    {d.invoice_no && <span className="font-medium text-gray-700">#{d.invoice_no}</span>}
                                    <span className="flex flex-wrap gap-x-3 gap-y-1">
                                      {(d.items || []).map((it, i) => (
                                        <span key={`${it.blend_name || it.category_id}-${it.roast_level}-${i}`} className="inline-flex items-center gap-1">
                                          {it.components?.length ? (
                                            <>
                                              {it.blend_name || it.components.map((c) => c.category_name).join(' + ')}
                                              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-100 text-violet-700">BLEND</span>
                                            </>
                                          ) : (
                                            <>{it.category_name} <LevelBadge level={it.roast_level} /></>
                                          )}
                                          {' '}{it.quantity_kg}kg
                                        </span>
                                      ))}
                                    </span>
                                    <span className="ml-auto font-semibold text-gray-800 whitespace-nowrap">
                                      {(d.total_quantity_kg || 0).toFixed(2)} kg
                                      {d.total_sale_value > 0 && <span className="text-emerald-600"> · {inr(d.total_sale_value)}</span>}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};
